import { fetchWithPolicy } from './fetchPolicy'

const ADMIN_CSRF_COOKIE_NAME = 'emporion_admin_csrf'
const ADMIN_CSRF_HEADER_NAME = 'x-emporion-csrf-token'
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

let runtimeCsrfToken: string | null = null

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null
  const pairs = document.cookie.split(';')
  for (const pair of pairs) {
    const [nameRaw, ...valueParts] = pair.split('=')
    if ((nameRaw ?? '').trim() !== name) continue
    const encoded = valueParts.join('=').trim()
    if (!encoded) return null
    try {
      const decoded = decodeURIComponent(encoded).trim()
      return decoded.length > 0 ? decoded : null
    } catch {
      return null
    }
  }
  return null
}

export const getAdminCsrfToken = (): string | null => {
  if (runtimeCsrfToken) return runtimeCsrfToken
  runtimeCsrfToken = readCookie(ADMIN_CSRF_COOKIE_NAME)
  return runtimeCsrfToken
}

export const setAdminCsrfToken = (value: string | null) => {
  runtimeCsrfToken = value?.trim() || null
}

export const resolveCsrfHeaderForMethod = (method: string | undefined): Record<string, string> => {
  const normalizedMethod = (method || 'GET').toUpperCase()
  if (!UNSAFE_METHODS.has(normalizedMethod)) return {}
  const token = getAdminCsrfToken()
  return token ? { [ADMIN_CSRF_HEADER_NAME]: token } : {}
}

export const bootstrapAdminCsrfToken = async (apiBase: string): Promise<string | null> => {
  try {
    const response = await fetchWithPolicy({
      request: `${apiBase}/auth/csrf`,
      init: {
        method: 'GET',
        credentials: 'include',
      },
      retries: 0,
    })
    if (!response.ok) {
      const fallbackToken = readCookie(ADMIN_CSRF_COOKIE_NAME)
      setAdminCsrfToken(fallbackToken)
      return fallbackToken
    }

    const payload = (await response.json()) as { csrfToken?: string } | null
    const token = typeof payload?.csrfToken === 'string' ? payload.csrfToken.trim() : ''
    const resolved = token || readCookie(ADMIN_CSRF_COOKIE_NAME)
    setAdminCsrfToken(resolved)
    return resolved
  } catch {
    const fallbackToken = readCookie(ADMIN_CSRF_COOKIE_NAME)
    setAdminCsrfToken(fallbackToken)
    return fallbackToken
  }
}
